import React, { useEffect, useState } from "react";
import AdminLayout from "./Common/AdminLayout";
import DataTable from "./Common/DataTable";
import { FaEdit, FaPlus, FaTimes } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import {
  AddSkill,
  DeleteSkills,
  EditSkill,
  FetchSkill,
  UpdateSkill,
} from "../../Redux/Slices/Skill";
import toast from "react-hot-toast";
import "../../asset/new/css/admin-modern.css";

const Skill = () => {
  const [skillName, setSkillName] = useState("");
  const [skillType, setSkillType] = useState("");
  const [editData, setEditData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { skillsData } = useSelector((state) => state?.Skill);

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!skillName || !skillType) {
      toast.error("Please fill all fields");
      return;
    }
    let inputData = { name: skillName, type: skillType };
    await AddSkill(inputData);
    let data = {
      page: 1,
      search: searchQuery
    };
    dispatch(FetchSkill(data));
    setSkillName("");
    setSkillType("");
    setAddModalOpen(false);
    toast.success("Skill added successfully");
  };

  // Edit
  const handleEdit = async (id) => {
    const data = await EditSkill(id);
    setEditData(data?.data[0]);
    setEditModalOpen(true);
  };

  // Update
  const handleUpdate = async () => {
    if (!editData?.name || !editData?.type) {
      toast.error("Please fill all fields");
      return;
    }
    let inputData = { name: editData.name, type: editData.type };
    await UpdateSkill(inputData, editData._id);
    let data = {
      page: 1,
      search: searchQuery
    };
    dispatch(FetchSkill(data));
    setEditModalOpen(false);
    toast.success("Skill updated successfully");
  };

  // Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this skill?")) {
      await DeleteSkills(id);
      let data = {
        page: 1,
        search: searchQuery
      };
      dispatch(FetchSkill(data));
      toast.success("Skill deleted successfully");
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    let data = {
      page: 1,
      search: value
    };
    dispatch(FetchSkill(data));
  };

  useEffect(() => {
    let data = {
      page: 1,
      search: searchQuery
    };
    dispatch(FetchSkill(data));
    document.title = "Skills | Admin";
  }, [dispatch]);

  // Format the date
  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    return dateObject.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handlePageChange = async (page) => {
    let data = {
      page: page,
      search: searchQuery
    };
    dispatch(FetchSkill(data));
  };

  // Table columns configuration
  const columns = [
    {
      key: "index",
      label: "ID",
      width: "80px",
      render: (row, index) => index + 1
    },
    {
      key: "name",
      label: "Skill Name",
      sortable: true,
      render: (row) => (
        <span style={{ fontWeight: 500 }}>{row.name}</span>
      )
    },
    {
      key: "type",
      label: "Type",
      sortable: true,
      render: (row) => (
        <span className={`admin-badge ${row.type === 'IT' ? 'admin-badge-info' : 'admin-badge-success'}`}>
          {row.type}
        </span>
      )
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatDate(row.created_at)
    },
    {
      key: "actions",
      label: "Actions",
      width: "200px",
      render: (row) => (
        <div className="admin-flex admin-gap-sm">
          <button
            onClick={() => handleEdit(row._id)}
            className="admin-btn admin-btn-sm admin-btn-primary"
            title="Edit"
          >
            <FaEdit /> Edit
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="admin-btn admin-btn-sm admin-btn-danger"
            title="Delete"
          >
            <AiFillDelete /> Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <AdminLayout
      title="Skills Management"
      subtitle="Manage functional and IT skills"
      breadcrumbs={[
        { label: "Master Settings", active: false },
        { label: "Skills", active: true }
      ]}
      actions={
        <button
          onClick={() => setAddModalOpen(true)}
          className="admin-btn admin-btn-primary"
        >
          <FaPlus /> Add Skill
        </button>
      }
    >
      <DataTable
        title="All Skills"
        totalCount={skillsData?.pagination?.totalResults || 0}
        columns={columns}
        data={skillsData?.docs || []}
        pagination={skillsData?.pagination}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        searchPlaceholder="Search skills..."
      />

      {/* Add Modal */}
      {addModalOpen && (
        <div className="admin-modal-backdrop" onClick={() => setAddModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">Add New Skill</h3>
              <button
                className="admin-modal-close"
                onClick={() => setAddModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label className="admin-form-label admin-form-label-required">
                    Skill Type
                  </label>
                  <select
                    className="admin-form-select"
                    value={skillType}
                    onChange={(e) => setSkillType(e.target.value)}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Functional">Functional</option>
                    <option value="IT">IT</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label admin-form-label-required">
                    Skill Name
                  </label>
                  <input
                    type="text"
                    className="admin-form-input"
                    placeholder="Enter skill name"
                    value={skillName}
                    onChange={(e) => setSkillName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => setAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  Add Skill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editData && (
        <div className="admin-modal-backdrop" onClick={() => setEditModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">Edit Skill</h3>
              <button
                className="admin-modal-close"
                onClick={() => setEditModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label admin-form-label-required">
                  Skill Type
                </label>
                <select
                  className="admin-form-select"
                  value={editData?.type || ""}
                  onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Functional">Functional</option>
                  <option value="IT">IT</option>
                </select>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label admin-form-label-required">
                  Skill Name
                </label>
                <input
                  type="text"
                  className="admin-form-input"
                  placeholder="Enter skill name"
                  value={editData?.name || ""}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={handleUpdate}
              >
                Update Skill
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Skill;
